import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { selectUserDetailsData } from 'src/app/store/user-details/user-details.selectors';
import { SettingsService } from 'src/app/shared/services/settings.service';
import { LocalStoreService } from 'src/app/shared/services/local-store.service';
import { HomeAnnouncement, HomeAnnouncementCopy } from './home-announcement.types';

// Home announcement banner — a short, dismissible message above the streaming
// player, aimed at a market rather than at everyone.
//
// Rules it deliberately follows:
//   * NEVER an overlay and NEVER sticky. It sits in the content flow and
//     scrolls away like anything else — that is what keeps it from nagging.
//   * Dismissal is remembered against a hash of the CONTENT, so closing it is
//     permanent for that message, but editing the text brings it back. One
//     dismissal must not silence every future announcement.
//   * Audience is decided by CURRENCY (the same rule the payment module uses),
//     so it can never be shown to someone who cannot act on it, and opening a
//     new market is a settings edit rather than a code change.
//   * The CTA is an INTERNAL route, validated against an allowlist: a settings
//     row is ops-editable data, and rendering an arbitrary URL from it would
//     be an open-redirect waiting to happen.
@Component({
  selector: 'app-home-announcement',
  templateUrl: './home-announcement.component.html',
  styleUrls: ['./home-announcement.component.scss'],
})
export class HomeAnnouncementComponent implements OnInit, OnDestroy {
  visible = false;
  badge: string | null = null;
  title: string | null = null;
  body: string | null = null;
  ctaLabel: string | null = null;
  route: string | null = null;

  private readonly isBrowser: boolean;
  private dismissKey: string | null = null;
  private destroy$ = new Subject<void>();

  // Only routes this app actually owns may be linked from a settings row.
  // `requires` records a route's own guard: /deposit-withdrawal-tn is gated by
  // TndPageGuard, so offering it to a non-TND audience would render a button
  // that bounces. When the audience and the destination disagree we drop the
  // CTA and keep the message — better a banner with no button than a button
  // that goes nowhere. Verified against ssrui's route table; every entry here
  // resolves (a wrong slug lands users on the 404 page).
  private static readonly ALLOWED_ROUTES: { path: string; requires?: string }[] = [
    { path: '/deposit-withdrawal-tn', requires: 'TND' },
    { path: '/bet-types' },
    { path: '/terms-and-conditions' },
    { path: '/account/transactions' },
    { path: '/account/wallet-transactions' },
  ];

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private settingsService: SettingsService,
    private authService: AuthService,
    private store: Store,
    private translate: TranslateService,
    private localStore: LocalStoreService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return; // never rendered server-side: it depends on the user's account
    }
    combineLatest([
      this.settingsService.homeAnnouncement$,
      this.authService.authStatusSub,
      this.store.select(selectUserDetailsData),
      // An in-place language switch must re-resolve the copy, otherwise an
      // English session keeps showing French text until a full reload.
      this.translate.onLangChange.pipe(startWith(null)),
    ])
      .pipe(
        map(([announcement, credential, details]) =>
          this.resolve(announcement, credential, details)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((ok) => (this.visible = ok));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  dismiss(): void {
    this.visible = false;
    if (this.dismissKey) {
      this.localStore.setItem(this.dismissKey, true);
    }
  }

  // -- internals ---------------------------------------------------------

  private resolve(
    announcement: HomeAnnouncement | null,
    credential: any,
    details: any
  ): boolean {
    if (!announcement || !announcement.enabled) {
      return false;
    }
    // Logged-in only: an anonymous visitor has no account currency, so there
    // is no honest way to decide whether the message is for them.
    if (!credential || !credential.uid) {
      return false;
    }
    const currency =
      (details &&
        (details.countryCurrency || (details.country && details.country.currency))) ||
      '';
    const wanted = announcement.currencies || [];
    if (wanted.length > 0 && wanted.indexOf(currency) === -1) {
      return false;
    }

    const copy = this.copyFor(announcement);
    if (!copy || !copy.title) {
      return false; // nothing worth showing
    }
    this.badge = this.badgeFor(announcement);
    this.title = copy.title;
    this.body = copy.body || null;
    this.ctaLabel = copy.cta || null;
    this.route = this.safeRoute(announcement.route, currency);

    // Hash everything the user actually sees: fixing a wrong route or a badge
    // must also bring the banner back for people who dismissed the broken one.
    this.dismissKey =
      'rb_ann_' +
      this.hash(
        JSON.stringify({
          text: announcement.text || {},
          badge: announcement.badge || {},
          route: announcement.route || '',
        })
      );
    return !this.localStore.getItem(this.dismissKey);
  }

  // Current UI language, falling back to French (primary) then any provided.
  private copyFor(a: HomeAnnouncement): HomeAnnouncementCopy | null {
    const text = a.text || {};
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'fr') as string;
    const keys = Object.keys(text);
    const candidates = [text[lang], text.fr, keys.length ? text[keys[0]] : null];
    // A language block that exists but carries no title is useless — keep
    // looking rather than rendering an empty banner.
    return candidates.filter((c) => c && c.title)[0] || null;
  }

  private badgeFor(a: HomeAnnouncement): string | null {
    const badge = a.badge || {};
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'fr') as string;
    return badge[lang] || badge.fr || null;
  }

  private safeRoute(route: string | undefined, currency: string): string | null {
    if (!route) return null;
    const allowed = HomeAnnouncementComponent.ALLOWED_ROUTES.filter(
      (r) => r.path === route
    )[0];
    if (!allowed) return null;
    // The destination has its own guard and this viewer would fail it.
    if (allowed.requires && allowed.requires !== currency) return null;
    return allowed.path;
  }

  // Small non-cryptographic hash — this only has to change when the copy
  // changes, so a dismissed message reappears after an edit.
  private hash(input: string): string {
    let h = 5381;
    for (let i = 0; i < input.length; i++) {
      h = ((h << 5) + h + input.charCodeAt(i)) | 0;
    }
    return Math.abs(h).toString(36);
  }
}
